/**
 * Freeze V2 — Add Embryo Controls
 *
 * Controls for adding temp embryos to a fertilization row:
 * - Non-slaughterhouse: quantity input + add button
 * - Slaughterhouse: opens MassEmbryoDialog
 * - Discard selected temp embryos
 * - Add from grading grid
 */
import { memo, useCallback, useMemo, useState } from 'react';

import { Grid3X3, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { StageGradeCountRequest } from '@/services/freeze';

import { useFreezeForm, useFreezeSelection } from '../../contexts';
import {
  useAddIntEmbsMutation,
  useAddMassInterimEmbryosMutation,
  useDeactivateInterimEmbryosMutation,
} from '../../services/freezeApi';
import type { TempEmbryoResponse } from '../../services/freezeApi';
import { SLAUGHTER_HOUSE_SITES } from '../../utils/constants';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';
import { MassEmbryoDialog } from '../dialogs/MassEmbryoDialog';

type AddEmbryoControlsProps = {
  fertilizationId: string;
  cane: string | null;
  opuSite: string | null;
  freezeMethod: string | null;
  tempEmbryos: TempEmbryoResponse[];
  onRefresh: () => void;
};

export const AddEmbryoControls = memo(function AddEmbryoControls({
  fertilizationId,
  cane,
  opuSite,
  freezeMethod,
  tempEmbryos,
  onRefresh,
}: AddEmbryoControlsProps) {
  const dispatch = useDispatch();
  const { batchForm, gradingData, dispatch: formDispatch } = useFreezeForm();
  const { tempEmbryoIds, dispatch: selectionDispatch } = useFreezeSelection();
  const [quantity, setQuantity] = useState(1);
  const [showMassDialog, setShowMassDialog] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const { mutateAsync: addIntEmbryos, isPending: isAdding } =
    useAddIntEmbsMutation();
  const { mutateAsync: addMassEmbryos, isPending: isMassAdding } =
    useAddMassInterimEmbryosMutation();
  const { mutateAsync: deactivateEmbryos, isPending: isDeactivating } =
    useDeactivateInterimEmbryosMutation();

  const isSlaughterHouse = SLAUGHTER_HOUSE_SITES.includes(opuSite ?? '');

  const { stageGradeCounts, hasGradingData } = useMemo(() => {
    const counts: StageGradeCountRequest[] = [];
    for (const grade of [1, 2, 3]) {
      for (const stage of [4, 5, 6, 7, 8, 9]) {
        const val =
          gradingData[grade]?.[
            `s${stage}` as keyof (typeof gradingData)[number]
          ];
        if (val && val > 0) {
          counts.push({ stage, grade, count: val });
        }
      }
    }
    return { stageGradeCounts: counts, hasGradingData: counts.length > 0 };
  }, [gradingData]);

  const handleAddByQuantity = useCallback(async () => {
    if (quantity < 1) return;
    try {
      const isDtPlus =
        !freezeMethod?.toLowerCase().includes('vitrification') &&
        !freezeMethod?.toLowerCase().includes('glycerol');

      await addIntEmbryos({
        input: {
          fertID: fertilizationId,
          cane,
          g1Count: quantity,
          g2Count: 0,
          g1CountToCreate: quantity,
          g2CountToCreate: 0,
          grade1: null,
          grade2: null,
          freezeDay: batchForm.freezeDay?.toString() ?? '',
          storageLoc: batchForm.storageLocationId || undefined,
          graderID: batchForm.graderId || undefined,
          freezeTechID: batchForm.freezeTechId || undefined,
          freezer: batchForm.freezer ?? '',
          embsPerVitStraw: batchForm.embryosPerVitStraw ?? undefined,
          embsPerStraw: batchForm.embryosPerStraw ?? undefined,
          slaughterHouse: isSlaughterHouse,
        },
        type: 1,
        dtPlus: isDtPlus ? (batchForm.dtPlus ?? false) : false,
      });
      onRefresh();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Embryos Added',
          detail: `Added ${quantity} temp embryo(s)`,
          life: 3000,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Add Failed',
          detail: `${error}`,
          life: 3000,
        }),
      );
    }
  }, [
    quantity,
    fertilizationId,
    cane,
    freezeMethod,
    isSlaughterHouse,
    batchForm,
    addIntEmbryos,
    onRefresh,
    dispatch,
  ]);

  const handleMassCreate = useCallback(
    async (grade1Count: number, grade2Count: number) => {
      try {
        const isDtPlus =
          !freezeMethod?.toLowerCase().includes('vitrification') &&
          !freezeMethod?.toLowerCase().includes('glycerol');

        await addMassEmbryos({
          input: {
            fertID: fertilizationId,
            cane,
            g1Count: grade1Count,
            g2Count: grade2Count,
            freezeDay: batchForm.freezeDay?.toString() ?? '',
            storageLoc: batchForm.storageLocationId || undefined,
            graderID: batchForm.graderId || undefined,
            freezeTechID: batchForm.freezeTechId || undefined,
            freezer: batchForm.freezer ?? '',
          },
          dtplus: isDtPlus ? (batchForm.dtPlus ?? false) : false,
        });
        setShowMassDialog(false);
        onRefresh();
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Mass Creation Complete',
            detail: `Created ${grade1Count + grade2Count} embryos`,
            life: 3000,
          }),
        );
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Creation Failed',
            detail: `${error}`,
            life: 3000,
          }),
        );
      }
    },
    [
      fertilizationId,
      cane,
      freezeMethod,
      batchForm,
      addMassEmbryos,
      onRefresh,
      dispatch,
    ],
  );

  const handleDiscard = useCallback(async () => {
    const selectedForThisFert = tempEmbryoIds.filter((id) =>
      tempEmbryos.some((e) => e.embryoFreezeEntryId === id),
    );

    if (selectedForThisFert.length === 0) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No Selection',
          detail: 'Select temp embryos to discard',
          life: 3000,
        }),
      );
      return;
    }

    try {
      await deactivateEmbryos(selectedForThisFert);
      // Drop the discarded ids from the selection, otherwise they keep
      // counting as selected (Metrics showed 59 after deleting down to 58).
      const discarded = new Set(selectedForThisFert);
      selectionDispatch({
        type: 'SET_TEMP_IDS',
        ids: tempEmbryoIds.filter((id) => !discarded.has(id)),
      });
      onRefresh();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Embryos Discarded',
          detail: `Discarded ${selectedForThisFert.length} temp embryo(s)`,
          life: 3000,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Discard Failed',
          detail: `${error}`,
          life: 3000,
        }),
      );
    }
  }, [
    tempEmbryoIds,
    tempEmbryos,
    deactivateEmbryos,
    selectionDispatch,
    onRefresh,
    dispatch,
  ]);

  const [isAddingFromGrading, setIsAddingFromGrading] = useState(false);

  const handleAddFromGrading = useCallback(async () => {
    if (!hasGradingData) return;
    setIsAddingFromGrading(true);
    try {
      const isDtPlus =
        !freezeMethod?.toLowerCase().includes('vitrification') &&
        !freezeMethod?.toLowerCase().includes('glycerol');

      await addIntEmbryos({
        input: {
          fertID: fertilizationId,
          cane,
          g1Count: 1,
          g2Count: 0,
          freezeDay: batchForm.freezeDay?.toString() ?? '',
          storageLoc: batchForm.storageLocationId || undefined,
          graderID: batchForm.graderId || undefined,
          freezeTechID: batchForm.freezeTechId || undefined,
          freezer: batchForm.freezer ?? '',
          // Without these the API falls back to 1 embryo per straw, so a
          // vitrification setup came out as single (DT-style) straws.
          embsPerVitStraw: batchForm.embryosPerVitStraw ?? undefined,
          embsPerStraw: batchForm.embryosPerStraw ?? undefined,
          stageGradeCounts,
        },
        type: 1,
        dtPlus: isDtPlus ? (batchForm.dtPlus ?? false) : false,
      });

      const totalCount = stageGradeCounts.reduce((sum, c) => sum + c.count, 0);
      formDispatch({ type: 'CLEAR_GRADING' });
      onRefresh();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Grading Embryos Added',
          detail: `Created ${totalCount} embryo(s) from grading matrix`,
          life: 3000,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Add From Grading Failed',
          detail: `${error}`,
          life: 3000,
        }),
      );
    } finally {
      setIsAddingFromGrading(false);
    }
  }, [
    hasGradingData,
    stageGradeCounts,
    fertilizationId,
    cane,
    freezeMethod,
    batchForm,
    addIntEmbryos,
    formDispatch,
    onRefresh,
    dispatch,
  ]);

  const selectedCount = tempEmbryoIds.filter((id) =>
    tempEmbryos.some((e) => e.embryoFreezeEntryId === id),
  ).length;

  return (
    <div className="flex items-center gap-2 px-1">
      {!isSlaughterHouse && (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) =>
              setQuantity(Math.max(1, parseInt(e.target.value) || 1))
            }
            className="w-16 text-xs"
          />
        </div>
      )}

      <Button
        variant="ghost"
        size="xs"
        onClick={() =>
          isSlaughterHouse ? setShowMassDialog(true) : handleAddByQuantity()
        }
        disabled={isAdding || isMassAdding}
        title={
          isSlaughterHouse ? 'Mass create embryos' : `Add ${quantity} embryo(s)`
        }
      >
        <Plus className="size-3.5 text-green-600" />
        Add
      </Button>

      <Button
        variant="ghost"
        size="xs"
        onClick={() => setShowDiscardConfirm(true)}
        disabled={isDeactivating || selectedCount === 0}
        title="Discard selected temp embryos"
      >
        <Trash2 className="size-3.5 text-red-600" />
        Discard{selectedCount > 0 ? ` (${selectedCount})` : ''}
      </Button>

      {hasGradingData && (
        <Button
          variant="ghost"
          size="xs"
          onClick={handleAddFromGrading}
          disabled={isAddingFromGrading}
          title="Add embryos from grading matrix"
        >
          {isAddingFromGrading ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Grid3X3 className="size-3.5 text-blue-600" />
          )}
          Add From Grid
        </Button>
      )}

      <Button
        variant="ghost"
        size="xs"
        onClick={onRefresh}
        title="Refresh embryo data"
      >
        <RefreshCw className="size-3.5" />
      </Button>

      <MassEmbryoDialog
        open={showMassDialog}
        onOpenChange={setShowMassDialog}
        onConfirm={handleMassCreate}
        isLoading={isMassAdding}
      />

      <ConfirmDialog
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        title="Discard Temp Embryos"
        description={`Are you sure you want to discard ${selectedCount} selected temp embryo(s)? This cannot be undone.`}
        onConfirm={handleDiscard}
        confirmLabel="Discard"
        confirmVariant="destructive"
        isLoading={isDeactivating}
      />
    </div>
  );
});
